import { observer } from 'mobx-react-lite'
import type { Key } from 'react'
import { useRef, useState } from 'react'

import { ConnectedFeatureTooltip } from '@src/app/components'
import useFeatureTooltipsActions from '@src/app/components/FeatureTooltip/useFeatureTooltipsActions'
import { useAppStore } from '@src/app/context'
import { useCurrentUser } from '@src/app/user'
import { useCallsDialogContext } from '@src/app/voice/context/useCallsDialogContext'
import { useCallContext } from '@src/app/voice/ongoing-call/context/useCallContext'
import type { OngoingCallMode } from '@src/app/voice/ongoing-call/types'
import Button from '@src/component/ButtonV2'
import IconButton from '@src/component/IconButton'
import useKeyboardShortcuts from '@src/lib/hooks/useKeyboardShortcuts'
import isNonNull from '@src/lib/isNonNull'
import type { FeaturesPreviewTrigger } from '@src/service/analytics/WorkspaceAnalyticsStore'
import Menu from '@ui/Menu'
import Tooltip from '@ui/Tooltip'
import {
  AddUserIcon,
  CallEndIcon,
  CallHoldFullIcon,
  CallHoldIcon,
  CallHoldOffFullIcon,
  CallHoldOffIcon,
  CallKeypadIcon,
  CallLeaveIcon,
  CallMenuMicIcon,
  CallMicMuteIcon,
  CallMoreIcon,
  ListenerIcon,
  MenuTransferIcon,
  NumberpadIcon,
  RecordIcon,
  RecordStopIcon,
  SettingsIcon,
  WhisperIcon,
} from '@ui/icons/tint/20/general'
import { theme } from '@ui/theme'
import { LayerProvider } from '@ui/z-index'

import * as styles from './CallActions.css'
import EndButton from './EndButton'
import MuteButton from './MuteButton'
import RecordingButton from './RecordingButton'

interface CallActionsProps {
  mode: OngoingCallMode | null
  loading: boolean
  setMode(value: OngoingCallMode): void
  closeActionPanel(): void
  openUpgradeModal(trigger: FeaturesPreviewTrigger): void
}

const CallActions = ({
  mode,
  loading,
  setMode,
  closeActionPanel,
  openUpgradeModal,
}: CallActionsProps) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const moreRef = useRef<HTMLButtonElement>(null)

  const { call } = useCallContext()
  const user = useCurrentUser()
  const { blurred } = useCallsDialogContext()
  const { dismiss } = useFeatureTooltipsActions()
  const { service, toast, billing } = useAppStore()
  const conferenceCallingCapabilityEnabled =
    service.capabilities.features.conferenceCallingEnabled
  const callTransferCapabilityEnabled = service.capabilities.features.callTransferEnabled
  const holdCapabilityEnabled = service.capabilities.features.callHoldingEnabled
  const premiumPlanName = billing.getPlanName('premium')

  const addParticipantsVisible = mode === 'add-participant'
  const transferVisible = mode === 'transfer'
  const settingsVisible = mode === 'settings'
  const keypadVisible = mode === 'keypad'

  const toggleMute = () => {
    if (call.participants.current) {
      call.participants
        .update(call.participants.current.id, {
          isMuted: !call.participants.current.isMuted,
        })
        .catch(toast.showError)
    }
    call.toggleMute()
    setIsMenuOpen(false)
  }

  const toggleRecord = () => {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises -- UXP-3744 - Fix Promise-related ESLint issues
    call.toggleRecord()
    setIsMenuOpen(false)
  }

  const handleKeypad = () => {
    if (keypadVisible) {
      closeActionPanel()
    } else {
      setMode('keypad')
    }
    setIsMenuOpen(false)
  }
  const handleSettings = () => {
    if (settingsVisible) {
      closeActionPanel()
    } else {
      setMode('settings')
    }
    setIsMenuOpen(false)
  }

  const handleDisconnect = () => {
    call.leave()
    setIsMenuOpen(false)
  }

  const handleEndForEveryone = () => {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises -- UXP-3744 - Fix Promise-related ESLint issues
    call.endForEveryone()
    setIsMenuOpen(false)
  }

  const handleOnHold = () => {
    if (holdCapabilityEnabled) {
      call.toggleHold().catch(toast.showError)
    } else {
      openUpgradeModal('call hold')
    }
    setIsMenuOpen(false)
  }

  const handleStopWhispering = () => {
    if (call.participants.current) {
      call.participants
        .update(call.participants.current.id, { coaching: undefined })
        .catch(toast.showError)
    }
  }

  const handleAddParticipants = () => {
    if (conferenceCallingCapabilityEnabled) {
      if (addParticipantsVisible) {
        closeActionPanel()
      } else {
        setMode('add-participant')
      }
    } else {
      openUpgradeModal('group calling')
    }
    setIsMenuOpen(false)
  }

  const handleCallTransfer = () => {
    if (callTransferCapabilityEnabled) {
      if (transferVisible) {
        closeActionPanel()
      } else {
        setMode('transfer')
        dismiss('warmTransfer1', 'warmTransfer2')
      }
    } else {
      openUpgradeModal('call transfer')
    }
    setIsMenuOpen(false)
  }

  const onAction = (key: Key) => {
    switch (key) {
      case 'hold':
        handleOnHold()
        break
      case 'addParticipants':
        handleAddParticipants()
        break
      case 'transfer':
        handleCallTransfer()
        break
      case 'mute':
        toggleMute()
        break
      case 'record':
        toggleRecord()
        break
      case 'keypad':
        handleKeypad()
        break
      case 'settings':
        handleSettings()
        break
      case 'leave':
        handleDisconnect()
        break
      case 'end':
        handleEndForEveryone()
        break
    }
  }

  const menuItems = [
    call.hasExternalParticipants ? (
      call.isOnHold ? (
        <Menu.ListItem icon={<CallHoldOffIcon />} key="hold">
          Take call off hold
        </Menu.ListItem>
      ) : (
        <Menu.ListItem
          icon={
            <CallHoldIcon
              className={!holdCapabilityEnabled ? styles.menuItemDisabled : undefined}
            />
          }
          key="hold"
          textValue="Put call on hold"
        >
          <Tooltip
            title={`Upgrade to ${premiumPlanName} to unlock`}
            disabled={holdCapabilityEnabled}
            placement="top"
            crossOffset={3}
            colorScheme="call"
          >
            <span
              className={!holdCapabilityEnabled ? styles.menuItemDisabled : undefined}
            >
              Put call on hold
            </span>
          </Tooltip>
        </Menu.ListItem>
      )
    ) : null,
    <Menu.ListItem
      icon={
        <AddUserIcon
          className={
            !conferenceCallingCapabilityEnabled ? styles.menuItemDisabled : undefined
          }
        />
      }
      key="addParticipants"
      textValue={addParticipantsVisible ? 'Close add people panel' : 'Add people'}
    >
      <Tooltip
        title={`Upgrade to ${premiumPlanName} to unlock`}
        disabled={conferenceCallingCapabilityEnabled}
        placement="top"
        crossOffset={15}
        colorScheme="call"
      >
        <span
          className={
            !conferenceCallingCapabilityEnabled ? styles.menuItemDisabled : undefined
          }
        >
          {addParticipantsVisible ? 'Close add people panel' : 'Add people'}
        </span>
      </Tooltip>
    </Menu.ListItem>,
    !call.isGroupCall ? (
      <Menu.ListItem
        icon={
          <MenuTransferIcon
            className={
              !callTransferCapabilityEnabled ? styles.menuItemDisabled : undefined
            }
          />
        }
        key="transfer"
        textValue={transferVisible ? 'Close transfer call panel' : 'Transfer call'}
      >
        <LayerProvider layer="callDialog">
          <ConnectedFeatureTooltip
            name={'warmTransfer2'}
            showForUsersCreatedBefore="2023-08-24"
            isDisabled={!user.settings.web.newFeatureTooltips.warmTransfer1}
            autoFocus={false}
            shouldCloseOnScroll={false}
            shouldCloseOnInteractOutside={false}
            title="It’s right here"
            description='To transfer a call, navigate to the bottom action bar, open the more menu, and click on "Transfer call."'
            placement="left"
            offset={65}
            actions={[
              {
                type: 'secondary',
                title: 'Learn more',
                isOnCloseTriggered: true,
                onAction: () => {
                  window.open(
                    'https://support.openphone.co/hc/en-us/articles/1500009885361-How-to-transfer-calls',
                    '_blank',
                  )
                },
              },
              {
                type: 'primary',
                title: 'Got it',
                isOnCloseTriggered: true,
                onAction: () => setIsMenuOpen(false),
              },
            ]}
          >
            <span>
              <Tooltip
                title={`Upgrade to ${premiumPlanName} to unlock`}
                disabled={callTransferCapabilityEnabled}
                placement="top"
                crossOffset={12}
                colorScheme="call"
              >
                <span
                  className={
                    !callTransferCapabilityEnabled ? styles.menuItemDisabled : undefined
                  }
                >
                  {transferVisible ? 'Close transfer call panel' : 'Transfer call'}
                </span>
              </Tooltip>
            </span>
          </ConnectedFeatureTooltip>
        </LayerProvider>
      </Menu.ListItem>
    ) : null,
    <Menu.Separator key="separator1" />,
    call.isMuted ? (
      <Menu.ListItem icon={<CallMenuMicIcon />} key="mute">
        Unmute mic
      </Menu.ListItem>
    ) : (
      <Menu.ListItem icon={<CallMicMuteIcon />} key="mute">
        Mute mic
      </Menu.ListItem>
    ),
    call.recordingStatus === null ? null : call.isRecording ? (
      <Menu.ListItem icon={<RecordStopIcon />} key="record">
        Stop recording
      </Menu.ListItem>
    ) : (
      <Menu.ListItem icon={<RecordIcon />} key="record">
        Record call
      </Menu.ListItem>
    ),
    <Menu.ListItem icon={<NumberpadIcon />} key="keypad">
      {keypadVisible ? 'Close keypad panel' : 'Show keypad'}
    </Menu.ListItem>,
    <Menu.ListItem icon={<SettingsIcon />} key="settings">
      {settingsVisible ? 'Close audio settings panel' : 'Audio settings'}
    </Menu.ListItem>,
    <Menu.Separator key="separator2" />,
    call.isGroupCall ? (
      <Menu.ListItem icon={<CallLeaveIcon />} key="leave">
        Leave call
      </Menu.ListItem>
    ) : null,
    <Menu.ListItem
      icon={<CallEndIcon color={`rgb(${theme.palette.secondary.red.default})`} />}
      key="end"
    >
      {call.isGroupCall ? 'End call for all' : 'End call'}
    </Menu.ListItem>,
  ].filter(isNonNull)

  useKeyboardShortcuts({
    node: document,
    name: 'ongoing-call/mute',
    handler: (shortcut) => {
      if (shortcut === 'KeyM') {
        toggleMute()
      }
    },
  })

  return (
    <div className={styles.container}>
      <ConnectedFeatureTooltip
        name={'warmTransfer1'}
        showForUsersCreatedBefore="2023-08-24"
        isDisabled={loading || call.isGroupCall || !callTransferCapabilityEnabled}
        placement="top"
        autoFocus={false}
        shouldCloseOnScroll={false}
        shouldCloseOnInteractOutside={false}
        offset={8}
        openDelay={1000}
        hideArrow={true}
        title="✨ Warm transfer is here"
        description="Transfer a call to someone else while remaining on the line."
        onClose={() => {
          dismiss('warmTransfer1', 'warmTransfer2')
        }}
        actions={[
          {
            type: 'primary',
            title: 'Show me',
            onAction: () => {
              dismiss('warmTransfer1')
              setIsMenuOpen(true)
            },
          },
        ]}
      >
        <div className={styles.featureTooltipAnchor} />
      </ConnectedFeatureTooltip>
      {loading ? (
        <>
          <div></div>
          <EndButton
            title={'End call'}
            icon={<CallEndIcon />}
            handleDisconnect={handleEndForEveryone}
            className={styles.iconButton}
          />
        </>
      ) : call.isListener ? (
        call.participants.current?.coaching ? (
          <>
            <MuteButton toggleMute={toggleMute} className={styles.iconButton} />
            <div className={styles.actionsInTheMiddle}>
              {/* eslint-disable-next-line @typescript-eslint/no-deprecated */}
              <Button
                fullWidth
                color="textSecondary"
                variant="ghost"
                height={34}
                onClick={handleStopWhispering}
                startIcon={<WhisperIcon />}
                className={styles.actionsInTheMiddleButton}
              >
                Whispering...
              </Button>
            </div>
            <EndButton
              title={'Leave call'}
              icon={<CallLeaveIcon />}
              handleDisconnect={handleDisconnect}
              className={styles.iconButton}
            />
          </>
        ) : (
          <div className={styles.listener}>
            <Tooltip
              title="Manage audio settings"
              themeKey="dark"
              placement="top"
              colorScheme="call"
            >
              {/* eslint-disable-next-line @typescript-eslint/no-deprecated */}
              <Button
                fullWidth
                color="textSecondary"
                variant="ghost"
                height={34}
                onClick={handleSettings}
                startIcon={<ListenerIcon />}
                className={styles.actionsInTheMiddleButton}
              >
                Audio
              </Button>
            </Tooltip>
            {/* eslint-disable-next-line @typescript-eslint/no-deprecated */}
            <Button
              fullWidth
              color="textSecondary"
              variant="ghost"
              height={34}
              onClick={handleDisconnect}
              startIcon={<CallLeaveIcon />}
              className={styles.actionsInTheMiddleButton}
            >
              Leave call
            </Button>
          </div>
        )
      ) : (
        <>
          <MuteButton toggleMute={toggleMute} className={styles.iconButton} />
          <div className={styles.actionsInTheMiddle}>
            <RecordingButton className={styles.iconButton} />
            {/* eslint-disable-next-line custom-rules/no-deprecated-buttons, @typescript-eslint/no-deprecated -- FIXME: https://linear.app/openphone/issue/UXP-4347/migrate-deprecated-buttons-to-ds-button */}
            <IconButton
              icon={<CallKeypadIcon />}
              title={keypadVisible ? 'Hide keypad' : 'Show keypad'}
              color={keypadVisible ? 'white' : undefined}
              backgroundColor={
                keypadVisible
                  ? `rgb(${theme.palette.locked.call.highlight})`
                  : `rgb(${theme.palette.overlay.default})`
              }
              hoverColor="white"
              size={34}
              className={styles.iconButton}
              tooltip={{
                themeKey: 'dark',
                colorScheme: 'call',
                placement: 'top',
              }}
              onClick={handleKeypad}
            />
            {/* eslint-disable-next-line custom-rules/no-deprecated-buttons, @typescript-eslint/no-deprecated -- FIXME: https://linear.app/openphone/issue/UXP-4347/migrate-deprecated-buttons-to-ds-button */}
            <IconButton
              icon={call.isOnHold ? <CallHoldOffFullIcon /> : <CallHoldFullIcon />}
              title={
                !holdCapabilityEnabled
                  ? `Call holding is available only on ${premiumPlanName}`
                  : !call.hasExternalParticipants
                  ? 'Team members cannot be placed on hold'
                  : call.isOnHold
                  ? 'Take call off hold'
                  : 'Put call on hold'
              }
              color={call.isOnHold ? 'white' : undefined}
              backgroundColor={
                call.isOnHold
                  ? blurred
                    ? `rgb(${theme.palette.overlay.strong})`
                    : `rgb(${theme.palette.secondary.blue.default})`
                  : `rgb(${theme.palette.overlay.default})`
              }
              hoverColor="white"
              size={34}
              className={styles.iconButton}
              tooltip={{
                themeKey: 'dark',
                colorScheme: 'call',
                placement: 'top',
              }}
              onClick={handleOnHold}
              disabled={!holdCapabilityEnabled || !call.hasExternalParticipants}
            />
            <Menu.Provider
              isOpen={isMenuOpen}
              targetRef={moreRef}
              onClose={() => setIsMenuOpen(false)}
            >
              <Menu.List onAction={onAction} themeKey="dark" placement="top right">
                {...menuItems}
              </Menu.List>
            </Menu.Provider>
            {/* eslint-disable-next-line custom-rules/no-deprecated-buttons, @typescript-eslint/no-deprecated -- FIXME: https://linear.app/openphone/issue/UXP-4347/migrate-deprecated-buttons-to-ds-button */}
            <IconButton
              ref={moreRef}
              icon={<CallMoreIcon />}
              title={'More actions'}
              color={undefined}
              backgroundColor={`rgb(${theme.palette.overlay.default})`}
              hoverColor="white"
              size={34}
              className={styles.iconButton}
              tooltip={{
                themeKey: 'dark',
                colorScheme: 'call',
                placement: 'top',
              }}
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            />
          </div>
          <EndButton
            title={call.isGroupCall ? 'Leave call' : 'End call'}
            icon={call.isGroupCall ? <CallLeaveIcon /> : <CallEndIcon />}
            handleDisconnect={call.isGroupCall ? handleDisconnect : handleEndForEveryone}
            className={styles.iconButton}
          />
        </>
      )}
    </div>
  )
}

export default observer(CallActions)
